import { ESupplyTypes } from "src/utils/helpers";
import { Address } from "@origin-digital/address-lookup";
import { stringToAustralianStateCode } from "@origin-digital/node-location";
import {
  ContactPreference,
  ContactPreferenceProp,
} from "../../../screens/UpdateContactPrefsScreen/ContactOption";
import {
  LpgProfileAccountDetails_viewer_accounts,
  LpgProfileAccountDetails_viewer_accounts_billing,
} from "../../lpg/graphql-types/LpgProfileAccountDetails";
import { ProfileFieldMapTypes } from "../../profileFieldMapContextHelpers";
import {
  AustralianAddressInput,
  DeliveryType,
  LevelType,
  PostalDeliveryType,
  StreetSuffix,
  StreetType,
  UnitType,
} from "../../lpg/graphql-types/globalTypes";
import {
  floorTypeMapping,
  mapPafTypeToLPGType,
  postalDeliveryTypeMapping,
  streetSuffixTypeMapping,
  streetTypeMapping,
  unitTypeMapping,
} from "../../../screens/UpdateAddress/addressMappingHelpers";

const getContactPreference = (deliveryType: DeliveryType) =>
  deliveryType === DeliveryType.POSTAL_MAIL
    ? ContactPreference.POSTAL_MAIL
    : ContactPreference.EMAIL;

export const mapToLpgContactPreferenceProps = (
  lpgAccount?: LpgProfileAccountDetails_viewer_accounts
): ContactPreferenceProp[] | null => {
  if (!lpgAccount || lpgAccount.services?.length === 0) {
    return null;
  }

  const preference = getContactPreference(lpgAccount.billing.type);
  const primaryAddress =
    lpgAccount.services?.find((service) => service.primary)?.address
      ?.formattedAddress || null;

  return [
    {
      id: lpgAccount.accountNumber,
      recipient: {
        address:
          lpgAccount.billing.postalAddress?.toUpperCase() || primaryAddress,
        email: lpgAccount.billing.email,
        shouldPromptChatOnEmpty: true,
      },
      supplyTypes: [ESupplyTypes.LPG],
      preference,
      type: ProfileFieldMapTypes.LPG,
    },
  ];
};

export const mapBillingDetailsToContactPreferenceProp = (
  billing?: LpgProfileAccountDetails_viewer_accounts_billing
) => {
  if (!billing) {
    return undefined;
  }

  const preference = getContactPreference(billing.type);

  return {
    id: null,
    recipient: {
      email: billing.email,
      address: billing.postalAddress,
      shouldPromptChatOnEmpty: true,
    },
    supplyTypes: [ESupplyTypes.LPG],
    preference,
    type: ProfileFieldMapTypes.LPG,
  };
};

export const mapAddressToAustralianAddressInput = ({
  allotmentNumber,
  allPostalDeliveryTypesNumber,
  allPostalDeliveryTypesType,
  buildingName,
  buildingLevelNumber,
  buildingLevelType,
  buildingNumberFirst,
  buildingNumberLast,
  city,
  postcode,
  state,
  streetNameOnly,
  streetType,
  streetTypeSuffix,
  unitNumber,
  unitType,
}: Address): AustralianAddressInput => {
  return {
    postBoxNumber: allPostalDeliveryTypesNumber,
    postBoxType: mapPafTypeToLPGType(
      allPostalDeliveryTypesType?.toUpperCase(),
      postalDeliveryTypeMapping
    ) as PostalDeliveryType,
    levelNumber: buildingLevelNumber,
    levelType: mapPafTypeToLPGType(
      buildingLevelType?.toUpperCase(),
      floorTypeMapping
    ) as LevelType,
    unitNumber,
    unitType: mapPafTypeToLPGType(
      unitType?.toUpperCase(),
      unitTypeMapping
    ) as UnitType,
    buildingName,
    lotNumber: allotmentNumber,
    streetNumber: buildingNumberFirst,
    streetNumberLast: buildingNumberLast,
    streetName: streetNameOnly,
    streetType: mapPafTypeToLPGType(
      streetType?.toUpperCase(),
      streetTypeMapping
    ) as StreetType,
    streetSuffix: mapPafTypeToLPGType(
      streetTypeSuffix?.toUpperCase(),
      streetSuffixTypeMapping
    ) as StreetSuffix,
    locality: city,
    postcode,
    state: stringToAustralianStateCode(state),
  };
};
